import {
  Component,
  Element,
  Event,
  EventEmitter,
  Fragment,
  h,
  Host,
  JSX,
  Listen,
  Prop,
  State,
} from "@stencil/core";
import { getPPNDealersByRegion } from "../../services/dealer/dealer-service";
import { splitLocale } from "@phn/entities/locale";
import { City, DealersByRegion } from "@phn/entities/dealer-response";
import { dealerSearchResultsStore } from "../../state/dealer-search-results-store";
import { getErrorMessage } from "@phn/utility/navi-error";
import Logger from "@phn/services/logger";
import { isString } from "@phn/utility/helper";
import { componentsOnReady } from "@phn/utility/ready";
import { Timeout } from "@phn/utility/timeout";
import {
  DEALER_SEARCH_RESULT_DISPLAY_ELEMENT_ID_DOUBLE_DRAWER,
  ElementTypes,
  EventAction,
  EventActions,
  GEOLOCATION_BUTTON_SEARCH_TERM,
} from "@phn/utility/constants";
import { navContentStore } from "@phn/state/nav-content-store";
import { GlobalDataLayerService } from "@phn/services/analytics/global-data-layer-service";
import analyticsManager from "../../services/analytics/analytics-manager";
import ErrorMessage from "../dealer-search-form/error-message";

@Component({
  tag: "phn-nd-dealer-search-form-dropdown",
  styleUrl: "dealer-search-form-dropdown.scss",
  scoped: true,
})
export class DealerSearchFormDropdown {
  @Prop() locale = "";

  @Prop() breakpoint!: number;

  @State() errorMessage: string | null = null;

  @State() dealersOfRegion: DealersByRegion = { regions: [] };

  @State() cities: City[] = [];

  @State() selectedProvince = "";

  @State() selectedCity = "";

  @State() showSkeleton = true;

  @Event() dealerSearchFormEvent!: EventEmitter;

  @Event() clearSearchEvent!: EventEmitter;

  @Event() removeSkeletonOnDealerSearchForm!: EventEmitter;

  @Event() loadingResults!: EventEmitter<boolean>;

  @Element() el!: HTMLPhnNdDealerSearchFormDropdownElement;

  componentWillLoad(): void {
    this.waitForComponentsToLoad();
    this.getDealersByRegion();
  }

  componentDidLoad(): void {
    this.emitPageLoadEvent();
  }

  private async waitForComponentsToLoad() {
    await componentsOnReady(this.el, new Timeout(5000));
    this.removeSkeletonOnDealerSearchForm.emit();
  }

  @Listen("removeSkeletonOnDealerSearchForm")
  loadedAutoCompleteListener(_event: Event): void {
    this.showSkeleton = false;
  }

  private async getDealersByRegion() {
    const { error: errorContent } = navContentStore.state;
    const { country } = splitLocale(this.locale);

    try {
      this.loadingResults.emit(true);
      this.dealersOfRegion = await getPPNDealersByRegion(country);
    } catch (err) {
      Logger.error("FETCH_DEALERS", err as Error);
      this.errorMessage = getErrorMessage(errorContent, err as Error);
    } finally {
      this.loadingResults.emit(false);
      this.dealerSearchFormEvent.emit();
    }
  }

  private onCityClick(): void {
    this.emitDropDownClickEvent("", "City", EventActions.DEALER_DROPDOWN_CLICK);
  }

  private getLocalizedCityName(city: City, language: string): string {
    if (language === "zh") {
      return (
        Object.values(city.dealers)[0]?.ppnDealer?.addressLocalized?.city ||
        city.cityName
      );
    }
    return city.cityName;
  }

  private onCitySelect(event: Event): void {
    const detail = (event as CustomEvent<{ value: string }>).detail;
    /* istanbul ignore if */
    if (!detail) {
      return;
    }

    const selectedCity = detail.value;
    this.selectedCity = selectedCity;
    const { language } = splitLocale(this.locale);
    dealerSearchResultsStore.state.ppnDealers =
      this.cities.find((city) =>
        [city.cityName, city.cityNameLocalized, this.getLocalizedCityName(city, language)].includes(selectedCity),
      )?.dealers || {};
    dealerSearchResultsStore.state.results = Object.keys(
      dealerSearchResultsStore.state.ppnDealers,
    );
    dealerSearchResultsStore.state.searchTerm = `${this.selectedProvince}, ${this.selectedCity}`;

    this.emitDropDownClickEvent(
      selectedCity,
      "City",
      EventActions.DEALER_DROPDOWN_SELECT_CLICK,
    );
    this.emitResultsLoadEvent();
  }

  private onProvinceClick(): void {
    this.emitDropDownClickEvent(
      "",
      "Province",
      EventActions.DEALER_DROPDOWN_CLICK,
    );
  }

  private onProvinceSelect(event: Event): void {
    const detail = (event as CustomEvent<{ value: string }>).detail;
    /* istanbul ignore if */
    if (!detail) {
      return;
    }

    const selectedProvince = detail.value;
    this.cities = this.getCitiesByGivenRegionName(selectedProvince);
    this.selectedProvince = selectedProvince;
    this.selectedCity = "";

    this.emitDropDownClickEvent(
      selectedProvince,
      "Province",
      EventActions.DEALER_DROPDOWN_SELECT_CLICK,
    );
  }

  private getCitiesByGivenRegionName(province: string): City[] {
    const { regions } = this.dealersOfRegion;

    const selectedRegion = regions.find(({ regionName, regionNameLocalized }) =>
      [regionName, regionNameLocalized].includes(province),
    );

    return selectedRegion?.cities || [];
  }

  private emitPageLoadEvent() {
    const { results, searchTerm } = dealerSearchResultsStore.state;
    const { dealer } = navContentStore.state;

    GlobalDataLayerService.getInstance().pushDataLayerEvent(
      EventActions.DEALER_SEARCH_SCREEN_LOAD,
      {
        componentDisplay: {
          displayElementId: analyticsManager.calculateDisplayElementId(),
          displayElementType: analyticsManager.calculateDisplayElementType(
            "",
            ElementTypes.dealerSearchForm,
          ),
          displayElementName: dealer.text,
        },
        componentSearch: {
          searchTerm: isString(searchTerm)
            ? searchTerm
            : GEOLOCATION_BUTTON_SEARCH_TERM,
          numberOfResults: results?.length || 0,
        },
      },
    );
  }

  private emitResultsLoadEvent() {
    const { results, searchTerm } = dealerSearchResultsStore.state;
    const { dealer: dealerContent } = navContentStore.state;

    const numberOfResults = results?.length || 0;

    GlobalDataLayerService.getInstance().pushDataLayerEvent(
      EventActions.DEALER_RESULTS_SCREEN_LOAD,
      {
        componentDisplay: {
          displayElementId:
            analyticsManager.getDealerResultsLoadDisplayElementId(
              DEALER_SEARCH_RESULT_DISPLAY_ELEMENT_ID_DOUBLE_DRAWER,
            ),
          displayElementType:
            analyticsManager.getDealerResultsLoadDisplayElementType(),
          displayElementName: dealerContent.text,
        },
        componentSearch: {
          searchTerm,
          numberOfResults,
        },
      },
    );
  }

  private emitDropDownClickEvent(
    displayElementName: string,
    clickElementId: string,
    eventAction: EventAction,
  ) {
    GlobalDataLayerService.getInstance().pushDataLayerEvent(eventAction, {
      componentDisplay: {
        displayElementId: analyticsManager.calculateDisplayElementId(),
        displayElementType: analyticsManager.calculateDisplayElementType(
          "",
          ElementTypes.dealerSearchForm,
        ),
        ...(isString(displayElementName) ? { displayElementName } : {}),
      },
      componentClick: {
        clickElementType: "interaction",
        clickElementId,
        clickElementName: "dropdown",
      },
    });
  }

  private reset() {
    this.errorMessage = null;
    this.loadingResults.emit(false);
    this.selectedProvince = "";
    this.selectedCity = "";

    dealerSearchResultsStore.state.results = null;

    this.clearSearchEvent.emit();
  }

  render(): JSX.Element {
    const { results } = dealerSearchResultsStore.state;
    const { dealer: dealerContent } = navContentStore.state;
    const searchText = dealerSearchResultsStore.state.searchTerm;
    const { language } = splitLocale(this.locale);

    return (
      <Host>
        {results && results.length > 0 ? (
          <form
            class="form-container"
            onSubmit={(event) => {
              event.preventDefault();
            }}
          >
            <div>
              <div class="phn-flex-row phn-align-center phn-justify-between title-container">
                <div class="text-limit">
                  <phn-p-text
                    weight="regular"
                    size="small"
                    tag="div"
                    ellipsis="true"
                    color="contrast-medium"
                    data-test-id="dealer-search-form-input"
                  >
                    {searchText}
                  </phn-p-text>
                </div>
                <phn-p-icon
                  name="close"
                  class="close-icon"
                  style={{
                    display: this.showSkeleton ? "none" : "",
                    cursor: "pointer",
                  }}
                  onClick={(event: Event) => {
                    event.preventDefault();
                    this.reset();
                  }}
                />
              </div>
            </div>
          </form>
        ) : (
          <Fragment>
            <phn-p-select
              label={dealerContent.search.dropdown.province}
              style={{ display: this.showSkeleton ? "none" : "" }}
              onClick={() =>
                !isString(this.selectedProvince) ? this.onProvinceClick() : null
              }
              name="dealers-province"
              value={
                this.selectedProvince ||
                dealerContent.search.dropdown.selectAProvince
              }
              onChange={(event) => this.onProvinceSelect(event)}
            >
              <phn-p-select-option
                hidden
                value={dealerContent.search.dropdown.selectAProvince}
              >
                {dealerContent.search.dropdown.selectAProvince}
              </phn-p-select-option>
              {this.dealersOfRegion.regions.map((region) => {
                const province = language === "zh"
                  ? (region.regionNameLocalized ?? region.regionName)
                  : region.regionName;
                return (
                  <phn-p-select-option value={province}>
                    {province}
                  </phn-p-select-option>
                );
              })}
            </phn-p-select>

            <phn-p-select
              style={{ display: this.showSkeleton ? "none" : "" }}
              class="city-select-form-field"
              label={dealerContent.search.dropdown.city}
              onClick={() =>
                !isString(this.selectedCity) ? this.onCityClick() : null
              }
              disabled={!isString(this.selectedProvince)}
              name="dealers-cities"
              onChange={(event) => {
                this.onCitySelect(event);
              }}
              value={
                this.selectedCity || dealerContent.search.dropdown.selectACity
              }
            >
              <phn-p-select-option
                hidden
                value={dealerContent.search.dropdown.selectACity}
              >
                {dealerContent.search.dropdown.selectACity}
              </phn-p-select-option>
              {this.cities.map((city) => {
                const cityLabel = this.getLocalizedCityName(city, language);
                return (
                  <phn-p-select-option value={cityLabel}>
                    {cityLabel}
                  </phn-p-select-option>
                );
              })}
            </phn-p-select>

            {isString(this.errorMessage) ? (
              <ErrorMessage
                errorMessage={this.errorMessage}
                onDismiss={() => this.reset()}
              />
            ) : null}
          </Fragment>
        )}
      </Host>
    );
  }
}
